
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { showErrorToast } from "@/utils/errorHandling";
import type { ImportDataType, CSVRowData } from "../types";

interface ImportState {
  isImporting: boolean;
  importResults: {
    success: number;
    errors: number;
    errorMessages: string[];
  };
  importStep: "upload" | "map" | "preview" | "complete";
}

export function useImportProcess(dataType: ImportDataType) {
  const [csvData, setCSVData] = useState<CSVRowData[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mappedFields, setMappedFields] = useState<Record<string, string>>({});
  const [{ isImporting, importResults, importStep }, setState] = useState<ImportState>({
    isImporting: false,
    importResults: { success: 0, errors: 0, errorMessages: [] },
    importStep: "upload"
  });

  const handleCSVUpload = (data: CSVRowData[], headers: string[]) => {
    setCSVData(data);
    setHeaders(headers);
    setState(prev => ({ ...prev, importStep: "map" }));
  };

  const handleFieldMapping = (mapping: Record<string, string>) => {
    setMappedFields(mapping);
    setState(prev => ({ ...prev, importStep: "preview" }));
  };

  const handleImport = async () => {
    setState(prev => ({ ...prev, isImporting: true }));
    const results = { success: 0, errors: 0, errorMessages: [] as string[] };

    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || !authData?.user?.id) {
        throw new Error(authError?.message || "User not authenticated");
      }
      
      const userId = authData.user.id;
      
      // Define the structure based on the dataType to match the expected type
      type InsertDataType = {
        user_id: string;
        [key: string]: any;
      };

      // Transform data with the correct type
      const transformedData: InsertDataType[] = csvData.map(row => {
        const transformedRow: InsertDataType = { user_id: userId };
        
        // Add mapped fields from CSV to the transformed data
        Object.entries(mappedFields).forEach(([dbField, csvHeader]) => {
          if (csvHeader && csvHeader !== "ignore") {
            // Handle special case for skills field which should be an array for candidates
            if (dataType === 'candidates' && dbField === 'skills' && row[csvHeader]) {
              transformedRow[dbField] = row[csvHeader].split(',').map((skill: string) => skill.trim());
            } else {
              transformedRow[dbField] = row[csvHeader];
            }
          }
        });
        
        return transformedRow;
      });

      const batchSize = 20;
      for (let i = 0; i < transformedData.length; i += batchSize) {
        const batch = transformedData.slice(i, i + batchSize);
        const { data, error } = await supabase
          .from(dataType)
          .insert(batch)
          .select();
        
        if (error) {
          results.errors += batch.length;
          results.errorMessages.push(`Batch ${Math.floor(i/batchSize) + 1} error: ${error.message}`);
        } else if (data) {
          results.success += data.length;
        }
      }

      setState(prev => ({
        ...prev,
        importResults: results,
        importStep: "complete",
        isImporting: false
      }));
    } catch (error) {
      showErrorToast(error, "Import failed");
      setState(prev => ({ ...prev, isImporting: false }));
    }
  };

  const resetImport = () => {
    setCSVData([]);
    setHeaders([]);
    setMappedFields({});
    setState({
      isImporting: false,
      importResults: { success: 0, errors: 0, errorMessages: [] },
      importStep: "upload"
    });
  };

  return {
    csvData,
    headers,
    mappedFields,
    isImporting,
    importResults,
    importStep,
    handleCSVUpload,
    handleFieldMapping,
    handleImport,
    resetImport
  };
}
